import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./providers/AuthProvider";
import { AdminLayout } from "./layouts/AdminLayout";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardHome } from "./pages/dashboard/DashboardHome";
import { DashboardAppointments } from "./pages/dashboard/DashboardAppointments";
import { DashboardClients } from "./pages/dashboard/DashboardClients";
import { DashboardRecurring } from "./pages/dashboard/DashboardRecurring";
import { DashboardWaitlist } from "./pages/dashboard/DashboardWaitlist";
import { DashboardPayments } from "./pages/dashboard/DashboardPayments";
import { DashboardServices } from "./pages/dashboard/DashboardServices";
import { DashboardPrograms } from "./pages/dashboard/DashboardPrograms";
import { DashboardCourts } from "./pages/dashboard/DashboardCourts";
import { DashboardSchedule } from "./pages/dashboard/DashboardSchedule";
import { DashboardCalendar } from "./pages/dashboard/DashboardCalendar";
import { DashboardSettings } from "./pages/dashboard/DashboardSettings";
import { DashboardNotifications } from "./pages/dashboard/DashboardNotifications";
import { DashboardSubscription } from "./pages/dashboard/DashboardSubscription";
import { DashboardTeam } from "./pages/dashboard/DashboardTeam";
import { DashboardAgendas } from "./pages/dashboard/DashboardAgendas";
import { DashboardAgendaStaff } from "./pages/dashboard/DashboardAgendaStaff";
import { SettingsLayout } from "./layouts/SettingsLayout";
import { DashboardBehavior } from "./pages/dashboard/DashboardBehavior";
import { DashboardRequests } from "./pages/dashboard/DashboardRequests";
import { DashboardQR } from "./pages/dashboard/DashboardQR";
import { DashboardBlockedDates } from "./pages/dashboard/DashboardBlockedDates";
import { DashboardSettingsCustomFields } from "./pages/dashboard/DashboardSettingsCustomFields";
import { DashboardGoogleCalendar } from "./pages/dashboard/DashboardGoogleCalendar";
import { DashboardSupport } from "./pages/dashboard/DashboardSupport";
import { DashboardMarket } from "./pages/dashboard/DashboardMarket";
import { DashboardCobros } from "./pages/dashboard/DashboardCobros";
import { DashboardOdontogram } from "./pages/dashboard/DashboardOdontogram";
import { OdontogramEditor } from "./pages/dashboard/odontogram/OdontogramEditor";
import { OdontogramByClient } from "./pages/dashboard/odontogram/OdontogramByClient";
import { OdontogramByEmail } from "./pages/dashboard/odontogram/OdontogramByEmail";
import { OnboardingWizardProvider } from "./context/onboarding-wizard-context";
import { OnboardingWizardPage } from "./pages/onboarding/OnboardingWizardPage";
import { AdminHome } from "./pages/admin/AdminHome";
import { AdminProfessionalNew } from "./pages/admin/AdminProfessionalNew";
import { AdminProfessionalDetail } from "./pages/admin/AdminProfessionalDetail";
import { AdminAnnouncements } from "./pages/admin/AdminAnnouncements";
import { AdminLandingPlans } from "./pages/admin/AdminLandingPlans";
import { AdminSettings } from "./pages/admin/AdminSettings";
import { AdminNotifications } from "./pages/admin/AdminNotifications";
import { AdminTickets } from "./pages/admin/AdminTickets";
import { AdminPlanes } from "./pages/admin/AdminPlanes";
import { AdminDiscountCodes } from "./pages/admin/AdminDiscountCodes";
import { AdminReferralCodes } from "./pages/admin/AdminReferralCodes";
import { AdminReferralCodeDetail } from "./pages/admin/AdminReferralCodeDetail";
import { AdminCosts } from "./pages/admin/AdminCosts";
import { AdminAccessLogs } from "./pages/admin/AdminAccessLogs";
import { BookPage } from "./pages/book/BookPage";
import { BookDemoPage } from "./pages/book/BookDemoPage";
import { BookConfirmPage } from "./pages/book/BookConfirmPage";
import { ManageBookingPage } from "./pages/book/ManageBookingPage";
import { LoadingFrame } from "./components/ui/loading-frame";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";
import { ConfirmEmailChangePage } from "./pages/auth/ConfirmEmailChangePage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { OrgBookingPage } from "./pages/OrgBookingPage";
import { PublicProfilePage } from "./pages/profile/PublicProfilePage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { InstallIosPage } from "./pages/InstallIosPage";

function Protected({
  role,
  children,
}: {
  role: "PROFESSIONAL" | "SUPERADMIN";
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ padding: 48, maxWidth: 560, margin: "0 auto" }}>
        <LoadingFrame variant="light" minimal minHeight={72} />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth/login" replace />;
  if (user.role !== role) {
    return <Navigate to={user.role === "SUPERADMIN" ? "/admin" : "/dashboard"} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/confirm-email-change" element={<ConfirmEmailChangePage />} />

      <Route
        path="/onboarding"
        element={
          <Protected role="PROFESSIONAL">
            <OnboardingWizardProvider>
              <OnboardingWizardPage />
            </OnboardingWizardProvider>
          </Protected>
        }
      />

      <Route
        path="/dashboard"
        element={
          <Protected role="PROFESSIONAL">
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="calendar" element={<DashboardCalendar />} />
        <Route path="services" element={<DashboardServices />} />
        <Route path="programs" element={<DashboardPrograms />} />
        <Route path="courts" element={<DashboardCourts />} />
        <Route path="market" element={<DashboardMarket />} />
        <Route path="cobros" element={<DashboardCobros />} />
        <Route path="odontogram" element={<DashboardOdontogram />} />
        <Route path="odontogram/by-client/:clientId" element={<OdontogramByClient />} />
        <Route path="odontogram/by-email/:email" element={<OdontogramByEmail />} />
        <Route path="odontogram/:id" element={<OdontogramEditor />} />

        <Route element={<SettingsLayout />}>
          <Route path="settings" element={<DashboardSettings />} />
          <Route path="settings/behavior" element={<DashboardBehavior />} />
          <Route path="settings/qr" element={<DashboardQR />} />
          <Route path="settings/custom-fields" element={<DashboardSettingsCustomFields />} />
          <Route path="settings/google-calendar" element={<DashboardGoogleCalendar />} />
          <Route path="settings/support" element={<DashboardSupport />} />
          <Route path="settings/reminders" element={<Navigate to="/dashboard/notifications" replace />} />
          <Route path="schedule" element={<DashboardSchedule />} />
          <Route path="schedule/blocked-dates" element={<DashboardBlockedDates />} />
          <Route path="payments" element={<DashboardPayments />} />
          <Route path="notifications" element={<DashboardNotifications />} />
          <Route path="appointments" element={<DashboardAppointments />} />
          <Route path="recurring" element={<DashboardRecurring />} />
          <Route path="waitlist" element={<DashboardWaitlist />} />
          <Route path="clients" element={<DashboardClients />} />
          <Route path="subscription" element={<DashboardSubscription />} />
          <Route path="team" element={<DashboardTeam />} />
          <Route path="agendas" element={<DashboardAgendas />} />
          <Route path="settings/agenda-staff" element={<DashboardAgendaStaff />} />
          <Route path="requests" element={<DashboardRequests />} />
        </Route>
      </Route>

      <Route
        path="/admin"
        element={
          <Protected role="SUPERADMIN">
            <AdminLayout />
          </Protected>
        }
      >
        <Route index element={<AdminHome />} />
        <Route path="professionals" element={<Navigate to="/admin/planes" replace />} />
        <Route path="professionals/new" element={<AdminProfessionalNew />} />
        <Route path="professionals/:id" element={<AdminProfessionalDetail />} />
        <Route path="announcements" element={<AdminAnnouncements />} />
        <Route path="landing-plans" element={<AdminLandingPlans />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="costs" element={<AdminCosts />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="access-logs" element={<AdminAccessLogs />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="planes" element={<AdminPlanes />} />
        <Route path="discount-codes" element={<AdminDiscountCodes />} />
        <Route path="referral-codes" element={<AdminReferralCodes />} />
        <Route path="referral-codes/:id" element={<AdminReferralCodeDetail />} />
        <Route path="subscriptions" element={<Navigate to="/admin/landing-plans" replace />} />
      </Route>

      <Route path="/p/:slug" element={<PublicProfilePage />} />
      <Route path="/demo-booking" element={<BookDemoPage />} />
      <Route path="/book/:slug" element={<BookPage />} />
      <Route path="/book/:slug/manage" element={<ManageBookingPage />} />
      <Route path="/book/:slug/confirm" element={<BookConfirmPage />} />
      <Route path="/org/:slug/booking" element={<OrgBookingPage />} />
      <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/privacidad" element={<PrivacyPolicyPage />} />
      <Route path="/instalar-ios" element={<InstallIosPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
